import React, {FC, useEffect} from 'react';

import loadable from '@loadable/component';
import {productClickEvent} from '@optimaxdev/analytics/desktop/lib/productClickEvent/productClickEvent';

import {GENDER_FROM_POPUP} from 'constants/category';
import {TYPE} from 'constants/dyData';
import {apiConfig} from 'libs/api/api';
import {dispatchCustomEvent} from 'libs/events/events';
import {flags} from 'libs/rollout';
import {cookieStore, sessionStore} from 'libs/storage/storage';

import {useCheckCanadaByAkamai} from 'libs/utils/utils';

import {useExtraPopupData} from './hooks/useExtraPopupData';

export type PropsType = {
    selectorWelcomePopup?: string;
    selectorExitPopup?: string;
    selectorTriggerPopup?: string;
    selectorCanadaPopUp?: string;
    contextPageType?: (typeof TYPE)[keyof typeof TYPE];
    firstname?: Maybe<string>;
    lastname?: Maybe<string>;
    email?: Maybe<string>;
    isSubscribed?: boolean;
    isLoggedIn?: boolean;
    categoryId?: number;
    productSku?: string;
};

const Popup = loadable(
    () =>
        import(
            /* webpackChunkName: "SubscribePopup" */ 'desktop/components/subscribePopup/subscribePopup'
        ),
    {
        resolveComponent: components => components.SubscribePopup,
    },
);

/**
 * Component that load a remote subscribe popup from DY
 */
export const PopupDY: FC<PropsType> = React.memo(
    ({
        categoryId = '',
        contextPageType = 'OTHER',
        productSku = '',
        selectorCanadaPopUp = 'D_Canada_popup',
        selectorExitPopup = 'D_E_Popup',
        selectorTriggerPopup = 'D_T_Popup',
        selectorWelcomePopup = 'D_Popup',
    }) => {
        const {customer, isLoading, popupProps, trustpilot} = useExtraPopupData();
        const isCanada = useCheckCanadaByAkamai();

        useEffect(() => {
            const listener = (e: Event) => {
                const {detail} = e as CustomEvent;
                const {product} = detail;

                productClickEvent([product], {
                    category: 'Exit Popup',
                    dataSource: 'DY',
                    index: detail.index,
                });

                sessionStore.set('toPDPFrom', 'Exit Popup');
            };
            window.addEventListener('dynamicYield:EPopupProductClicked', listener);
            return () => {
                dispatchCustomEvent('mfPopup:unMount');
                window.removeEventListener('dynamicYield:EPopupProductClicked', listener);
            };
        }, []);

        useEffect(() => {
            const listener = (event: Event) => {
                const {detail} = event as CustomEvent;
                const selectedGender = detail.gender;

                if (selectedGender) {
                    cookieStore.set(
                        GENDER_FROM_POPUP,
                        flags.expGenderPopupD2.isEnabled() ? selectedGender : '0',
                    );
                }
            };

            window.addEventListener('popup:setDefaultGender', listener);

            return () => {
                window.removeEventListener('popup:setDefaultGender', listener);
            };
        }, []);

        if (__NODEJS__ || !flags.apiPopupsDY.isEnabled() || isLoading) return null;

        let additionalData;

        switch (contextPageType) {
            case 'PRODUCT':
                additionalData = {data: [productSku]};
                break;
            case 'CATEGORY':
                additionalData = {data: [categoryId.toString()]};
                break;
            default:
                additionalData = {};
        }

        const requestData = {
            context: {page: {type: contextPageType, ...additionalData}},
            selectors: [
                isCanada ? selectorCanadaPopUp : selectorWelcomePopup,
                selectorExitPopup,
                selectorTriggerPopup,
            ],
        };

        return (
            <Popup
                customer={customer}
                dyRequestData={requestData}
                extraProps={popupProps}
                instance={apiConfig.instancePopupBackend}
                instanceDY={apiConfig.instanceDyChoose}
                trustpilot={trustpilot}
            />
        );
    },
);

PopupDY.displayName = 'PopupDY';
